import { EventEmitter, ListenerFn } from "eventemitter3";

import { CoreConstants, RequestIdGenerator, GenericErrorResultModel, CoreRequestModel, CoreResponseModel } from "./common";

import { SignatureInput } from "./http-models";

type ExtensionResponseCallback = (response: any, coreSession: AbstractJavaSession) => void;

type QueryExtensionVersionCallback = (arg: GenericErrorResultModel | Object) => void;

abstract class WebExtMessageBroker 
{
    abstract init() : void;

    abstract dispose() : void;
    
    protected events: EventEmitter;

    constructor (protected wnd: Window) {
        this.events = new EventEmitter();
    }

    get_eventEmitter() : EventEmitter { return this.events; }
}

class DefaultWebExtMessageBroker extends WebExtMessageBroker
{
    private _requestListener: ListenerFn;

    private _responseListener: (ev: MessageEvent) => any;

    init(): void 
    {
        //toda request postada no emitter vai ir para a window contendo a extensão
        this._requestListener = (rawRequest) => {
            this.wnd.postMessage(rawRequest, "*");
        };
        this.events.on(CoreConstants.REQUEST_MSG_TYPE, this._requestListener);

        //toda mensagem de resposta que chegar na window deve ir para o emitter
        this._responseListener = (msgEvent: MessageEvent) => {
            //pega dados da mensagem vinda no evento e garante que é uma resposta da extensão de assinatura
            const msg = msgEvent.data;
            if (msg && msg.type === CoreConstants.RESPONSE_MSG_TYPE) {
                this.events.emit(CoreConstants.RESPONSE_MSG_TYPE, msg);
            }
        };
        this.wnd.addEventListener("message", this._responseListener);
    }
    
    dispose(): void
    {
        if (this._requestListener) {
            this.events.off(CoreConstants.REQUEST_MSG_TYPE, this._requestListener);
            this._requestListener = null;
        }
        if (this._responseListener) {
            this.wnd.removeEventListener("message", this._responseListener);
            this._responseListener = null;
        }

        this.wnd = null;
        this.events = null;
    }
}

class IFrameWebExtMessageBroker extends WebExtMessageBroker
{
    private _requestListener: ListenerFn;

    private _responseListener: (ev: MessageEvent) => any;

    init(): void 
    {
        //toda request postada no emitter vai ir para a window TOP
        this._requestListener = (rawRequest: CoreRequestModel) => {
            //tipo da mensagem muda para proxy
            rawRequest.type = CoreConstants.PROXY_REQUEST_MSG_TYPE;
            this.wnd.top.postMessage(rawRequest, "*");
        };
        this.events.on(CoreConstants.REQUEST_MSG_TYPE, this._requestListener);

        //mensagens de resposta da extensão acabam sendo redirecionadas para janela da extensão
        this._responseListener = (msgEvent: MessageEvent) => {
            //pega dados da mensagem vinda no evento e garante que é uma resposta da extensão de assinatura
            const msg = msgEvent.data;
            if (msg && msg.type === CoreConstants.PROXY_RESPONSE_MSG_TYPE) {
                this.events.emit(CoreConstants.RESPONSE_MSG_TYPE, msg);
            }
        };
        this.wnd.addEventListener("message", this._responseListener);
    }

    dispose(): void
    {
        if (this._requestListener) {
            this.events.off(CoreConstants.REQUEST_MSG_TYPE, this._requestListener);
            this._requestListener = null;
        }
        if (this._responseListener) {
            this.wnd.removeEventListener("message", this._responseListener);
            this._responseListener = null;
        }

        this.wnd = null;
        this.events = null;
    }
}

export class IFrameContainerWebExtMessageHub
{
    private myWindow: Window;
    private iframeWindow: Window;

    init(window: Window, iframe: HTMLIFrameElement)
    {
        //salva as janelas envolvidas no processo
        this.myWindow = window;
        this.iframeWindow = iframe.contentWindow;

        //trata mensagens que estejam chegando em nossa janela
        this.myWindow.addEventListener("message", (msgEvent: MessageEvent) =>
        {
            const msg = msgEvent.data;
            if (msg && typeof msg.type === "string") 
            {
                //caso for uma request vinda do iframe, vamos re-enviar ela para nossa janela com tipo certo (e como nós sendo source)
                if (msgEvent.source === this.iframeWindow && msg.type === CoreConstants.PROXY_REQUEST_MSG_TYPE) {
                    msg.type = CoreConstants.REQUEST_MSG_TYPE;
                    this.myWindow.postMessage(msg, "*");
                }
                //se for uma resposta vinda da extensão, vamos postar ela no iframe como tipo 'proxy'
                else if (msg.type === CoreConstants.RESPONSE_MSG_TYPE) 
                {
                    msg.type = CoreConstants.PROXY_RESPONSE_MSG_TYPE;
                    this.iframeWindow.postMessage(msg, "*");
                }
            }
        });
    }
}

interface RequestInfo
{
    readonly requestObj: any;
    readonly callback: (arg: any, coreSession: AbstractJavaSession) => void;
}

/** Classe que representa uma sessão java para assinatura
 */
export abstract class AbstractJavaSession
{
    /** Indica se sessão está pronta para uso */
    private isReady: boolean = false;

    /** Disparador de eventos de notificação */
    private events: EventEmitter = new EventEmitter();

    /** Lista de requisições pendentes */
    private pendingRequests: { [id: string] : RequestInfo } = {};

    /** Construtor
     * @param javaSessionId                 Id da sessão
     * @param statisticsEnabled             Define se deve coletar estatísticas
     * @param signatureResponseMaxInterval  Tempo máximo, em milisegundos, para esperar resposta da extensão
     * @param idGenerator                   Objeto responsável pela geração de id de requisições
     */
    constructor(
        private javaSessionId: string,
        private statisticsEnabled: boolean,
        private signatureResponseMaxInterval: number,
        private idGenerator: RequestIdGenerator
    ) { }

    protected abstract postJavaRequest(reqObj: any);

    //sub:_handleCancelResponse
    private handleCancelResponse(response: any) : void {
        //percorre todas as tarefas canceladas e remove da lista de pendentes
        const cancelled: string[] = response.result && <string[]>response.result.cancelledRequests;
        if (cancelled) {
            for (let i = 0, _i = cancelled.length; i < _i; ++i) {
                const cancelledId: string = cancelled[i];
                delete this.pendingRequests[cancelledId];
            }
        }
    }

    /** Verifica se sessão está pronta para utilização
     * @returns Verdadeiro se sessão está pronta para utilização, senão falso
     */
    is_ready(): boolean {
        return this.isReady;
    }

    /** Retorna id da sessão java
     * @returns Id da sessão java
     */
    get_javaSessionId(): string {
        return this.javaSessionId;
    }

    /** Verifica se é uma sessão compartilhada
     * @returns Verdadeiro se é uma sessão compartilhada, senão falso
     */
    is_shared(): boolean {
        return this.get_javaSessionId() === CoreConstants.SHARED_JAVA_SESSION_ID;
    }

    /** Evento disparado quando o estado da sessão muda para ready
     */
    onReady(handler: (any) => void) : void {
        this.events.once("ready", handler);
    }

    /** Interpreta respostas de requisições de assinatura. Chamando callback das requisições completadas
     * @param response  Resposta da requisição de assinatura
     */
    internalHandleResponse(response: any) : void {
        //procura request pendente para o ID da resposta
        const pendingReq: RequestInfo = this.pendingRequests[response.id];
        if (!pendingReq) {
            return console.error("[AbstractJavaSession] Resposta com ID '" + response.id + "' não possui request pendente associada. Resposta sendo ignorada");
        }
        //remove request da lista de pendentes, se não tiver próximo pacote
        if (!response.hasNext) {
            delete this.pendingRequests[response.id];
        }

        //se a resposta foi do comando de cancelamento, faz tratamento dela
        if (response.operation === CoreConstants.OP_CANCEL) {
            this.handleCancelResponse(response);
        }

        //executa callback da request, passando a resposta
        if (pendingReq.callback) {
            //executa callback
            try { pendingReq.callback(response, this); }
            catch (ex) {
                console.error("[AbstractJavaSession] Callback disparou exception");
                console.dir(response);
                console.dir(ex);
            }
        }
    }
    
    internalNotifyReadiness() : void {
        this.isReady = true;
        this.events.emit("ready", this);
    }

    /** Envia mensagem para extensão
     * @param operation       Operação a ser realizada pela extensão
     * @param args            Parâmetros para operação a ser realizada
     * @param callbackMethod  Callback para processar resposta
     * @returns Id da requisição
     */
    postRequest(operation: string, args: any, callbackMethod: ExtensionResponseCallback) : string {
        //cria ID para request
        const reqId = this.idGenerator.next(this.get_javaSessionId());

        //verifica se é para computarmos estatísticas (hoje só estamos interessados para requests 'SIGN')
        const calcStats = this.statisticsEnabled && operation === CoreConstants.OP_SIGN;

        //cria objeto de request
        const reqObj: any = {
            id: reqId,
            type: CoreConstants.REQUEST_MSG_TYPE,
            operation: operation,
            computeStatistics: calcStats
        };
        //adiciona argumentos, se houver
        if (args) reqObj.arguments = args;

        //adiciona informação da request como pendente
        this.pendingRequests[reqId] = { requestObj: reqObj, callback: callbackMethod };

        //posta para o java
        this.postJavaRequest(reqObj);

        //retorna id da request
        return reqId;
    }

    /** Lista certificados para utilizar na assinatura
     * @param callbackMethod  Callback para processar resposta
     * @param includeChain    Define se deve ser incluída toda a cadeia de certificados no retorno
     * @param reloadStore     Define se deve atualizar store de certificados
     * @returns Id da requisição gerada
     */
    listCertificates(callbackMethod: ExtensionResponseCallback, includeChain?: boolean, reloadStore?: boolean, afterTo: number = 0) : string {
        //monta argumentos para a listagem de certificados
        const args: any = {
            includeChain: !!includeChain,
            reloadStore: !!reloadStore,
            afterTo: afterTo
        };
        //posta request
        return this.postRequest(CoreConstants.OP_LIST_CERTS, args, callbackMethod);
    }

    /** Busca cadeia de certificados
     * @param certId          Id do certificado do qual se deseja buscar a cadeia de certificados
     * @param callbackMethod  Callback para processar resposta 
     * @returns Id da requisição gerada
     */
    getCertChain(certId: string, callbackMethod: ExtensionResponseCallback) : string {
        const args: any = { certID: certId };
        return this.postRequest(CoreConstants.OP_GET_CERT_CHAIN, args, callbackMethod);
    }

    /** Realiza assinatura em um lote de inputs
     * @param certId          Id do certifica a ser utilizado para assinatura
     * @param algorithm       Algorítmo da assinatura a ser utilizado
     * @param inputs          Lote de inputs para assinar
     * @param callbackMethod  Callback para processar resposta
     * @returns Id da requisição gerada
     */
    sign(certId: string, algorithm: string, inputs: SignatureInput[], callbackMethod: ExtensionResponseCallback) : string {
        const args: any = {
            certID: certId,
            algorithm: algorithm,
            inputs: inputs
        };

        //verifica se tem tempo customizado de retorno para enviar
        if (this.signatureResponseMaxInterval) args.responseMaxInterval = this.signatureResponseMaxInterval;

        return this.postRequest(CoreConstants.OP_SIGN, args, callbackMethod);
    }
    
    //sub:_cancelRequests
    private internalCancelRequests(requests: string[], callbackMethod: ExtensionResponseCallback) : string {
        //verifica se tem requests para filtrar
        const args: any = requests ? { requests: requests } : null;
        //posta request para o java
        return this.postRequest(CoreConstants.OP_CANCEL, args, callbackMethod);
    }

    /** Cancela uma requisição
     * @param requestId       Id da requisição a ser cancelada
     * @param callbackMethod  Callback para processar resposta
     * @returns Id da requisição de cancelamento
     */
    cancelRequest(requestId: string, callbackMethod: ExtensionResponseCallback) : string {
        //invoca cancelamento de requests
        return this.internalCancelRequests([requestId], callbackMethod);
    }

    /** Cancela todas as requisições pendentes
     * @param callbackMethod  Callback para processar resposta
     * @returns Id da requisição de cancelamento
     */
    cancelAllRequests(callbackMethod: ExtensionResponseCallback) : string {
        return this.internalCancelRequests(null, callbackMethod);
    }

    /** Cancelamento de todas as requests chamado no dispose
     */
    protected cancelAllRequestsOnDispose() : void {
        this.cancelAllRequests(null);
    }

    /** Limpa referências do objeto
     */
    public internalDispose() : void {
        this.cancelAllRequestsOnDispose();

        this.events = null;
        this.javaSessionId = null;
        this.pendingRequests = null;
        this.idGenerator = null;
    }
}

/** Classe que representa uma sessão do applet
 */
export class AppletSession extends AbstractJavaSession
{
    /** 
     * @param appletId           Id do applet
     * @param jnlpPath           Caminho do applet
     * @param logEnabled         Define se o log de assinatura está habilitado
     * @param signerCoreElement  Elemento html do script control do core de assinatura
     */
    constructor(
        private appletId: string,
        private jnlpPath: string,
        private logEnabled: boolean,
        private signerCoreElement: HTMLElement,
        javaSessionId: string,
        statisticsEnabled: boolean,
        signatureResponseMaxInterval: number,
        idGenerator: RequestIdGenerator)
    {
        super(javaSessionId, statisticsEnabled, signatureResponseMaxInterval, idGenerator);

        this.initializeApplet();
    }

    /** Retorna o id do applet
     * @returns Id do applet
     */
    get_appletId() : string {
        return this.appletId;
    }

    /** Envia mensagem para o applet
     * @param reqObj  Json com parâmtros para execução da requisição
     */
    protected postJavaRequest(reqObj: any) : void {
        //se não tem applet ID, é pq está fazendo dispose (não precisa enviar nada para o java)
        const appletId: string = this.get_appletId();
        if (!appletId) return;

        //converte request para json e envia para o applet
        const jsonStr: string = JSON.stringify(reqObj);
        (<any>document.getElementById(appletId)).executeRequest(jsonStr);
    }

    //sub:_initializeApplet
    private initializeApplet() : void {
        //cria elemento embed
        const jApplet: HTMLElement = document.createElement("embed");
        
        //atributos do elemento
        const attributes: { [key: string] : string } = {
            "id": this.get_appletId(),
            "type": "application/x-java-applet;version=1.6",
            "width": "0",
            "height": "0",
            "jnlp_href": this.jnlpPath,
            "scriptable": "true",
            "qs-response-delivery": "__QuickSoft_DigitalSignature_Web_SignerCore_handleAppletResponse",
            "qs-readiness-notification": "__QuickSoft_DigitalSignature_Web_SignerCore_notifyAppletReady",
            "qs-java-session": this.get_javaSessionId()
        };
        //põe os atributos no elemento
        for (let key in attributes) {
            jApplet.setAttribute(key, attributes[key]);
        }

        //bota como position fixed, indo para fora da página (display none ou visibility hidden faz com que o applet não funcione)
        jApplet.style.position = "fixed";
        jApplet.style.top = "-1000px";

        //verifica se tem que habilitar o log
        if (this.logEnabled) {
            jApplet.setAttribute("qs-enable-log", "true");
        }

        //se não for shared, cria uma jvm para cada embed
        if (!this.is_shared()) {
            jApplet.setAttribute("separate_jvm", "true");
        }

        //faz append como irmão do elemento do signerCore
        this.signerCoreElement.parentNode.insertBefore(jApplet, this.signerCoreElement.nextSibling);
    }

    /** Limpa referências do objeto
     */
    public internalDispose() : void {
        //remove elemento de applet
        const elem: HTMLElement = document.getElementById(this.get_appletId());
        elem.parentNode.removeChild(elem);

        this.appletId = null;

        super.internalDispose();
    }
}

/** Sessão de assinatura via extensão web 
 */
export class WebExtSession extends AbstractJavaSession
{
    private skipSessionRelease: boolean = false;

    /** Construtor
     * @param javaSessionId                 Id da sessão
     * @param statisticsEnabled             Define se deve coletar estatísticas
     * @param signatureResponseMaxInterval  Tempo máximo, em milisegundos, para esperar resposta da extensão
     * @param idGenerator                   Objeto responsável pela geração de id de requisições
     * @param messageBroker                 Message broker, para comunicação com extensão do navegador
     */
    constructor(
        javaSessionId                : string,
        statisticsEnabled            : boolean,
        signatureResponseMaxInterval : number,
        idGenerator                  : RequestIdGenerator,
        
        private messageBroker        : EventEmitter
    )
    {
        super(javaSessionId, statisticsEnabled, signatureResponseMaxInterval, idGenerator);
    }

    /** Verifica se sessão não precisa executar o release
     * @returns Verdadeiro se sessão não precisa executar o release, senão falso 
     */
    get_skipSessionRelease() : boolean {
        return this.skipSessionRelease;
    }

    /** Define se sessão não precisa executar o release
     * @param skipSessionRelease  Verdadeiro para a sessão não precisar executar o release, senão falso 
     */
    set_skipSessionRelease(skipSessionRelease: boolean) : void {
        this.skipSessionRelease = skipSessionRelease;
    }

    /** Envia mensagem de ping para a extensão
     * @param messageBroker Broker de mensagens para extensão
     */
    static sendPingCommand(messageBroker: EventEmitter) : void {
        //monta request de "ping"
        const reqObj = {
            id: "?",
            type: CoreConstants.REQUEST_MSG_TYPE,
            operation: CoreConstants.OP_EXT_PING
        };
        //posta request para extensão
        messageBroker.emit(CoreConstants.REQUEST_MSG_TYPE, reqObj);
    }

    /** Envia mensagem para a extensão
     * @param reqObj  Objeto com parâmetros para execução
     */
    protected postJavaRequest(reqObj: any) : void {
        //posta mensagem no broker
        this.messageBroker.emit(CoreConstants.REQUEST_MSG_TYPE, reqObj);
    }

    /** Cancela todas as requests pendentes, utilizado no dispose
     */
    protected cancelAllRequestsOnDispose() : void {
        //se esta sessão for compartilhada, apenas chama o método base de dispose de requisições
        if (this.is_shared()) {
            super.cancelAllRequestsOnDispose();
        }
        else {
            //envia comando de release da sessão, se esta sessão não for shared
            this.postRequest(CoreConstants.OP_EXT_RELEASE_SESSION, null, null);
        }
    }

    /** Verifica versão da extensão que o usuário tem instalada
     * @param callbackMethod  Callback para processamento do retorno
     * @param bypassCache     Define se deve limpa cache antes enviar requisição
     * @returns Id da requisição
     */
    getExtensionVersion(callbackMethod: ExtensionResponseCallback, bypassCache: boolean = false) : string {
        //argumentos para a obtenção da versão
        const args: any = bypassCache ? { bypassCache: true } : null;
        //posta requisição de versão da app
        return this.postRequest(CoreConstants.OP_EXT_VERSION, args, callbackMethod);
    }
}

/** Handler para mensagem de applet pronto para utilização
 * @param javaSessionId  Id da sessão criada
 */
export function __QuickSoft_DigitalSignature_Web_SignerCore_notifyAppletReady(javaSessionId: string) : void {
    //verifica se tem singleton registrado
    const instance = SignerCore.getInstance();
    if (!instance) return console.error("Recebido sinal de 'ready' do applet de assinatura, porém nenhuma instância de SignerCore está registrada");
    //verifica se foi informada a sessão java
    if (!javaSessionId) return console.error("Parâmetro 'javaSessionId' não foi informado no método de notificação de 'ready' para applet");
    
    //invoca tratamento de readiness de applet
    instance.internalHandleAppletReadyResponse(javaSessionId);
}

/** Handler para respostas de requisições ao applet. Transforma json retornado em objeto
 * @param rawResponse  Json com retorno da requisição
 */
export function __QuickSoft_DigitalSignature_Web_SignerCore_handleAppletResponse(rawResponse: string) : void {
    //verifica se tem singleton registrado
    const instance = SignerCore.getInstance();
    if (!instance) return console.error("Recebida response do applet de assinatura, porém nenhuma instância de SignerCore está registrada");

    //como resposta veio do applet, ela está em string json (temos de converter para objeto). Chama o tratamento de resposta
    const respObj = JSON.parse(rawResponse);
    instance.internalHandleResponse(respObj);
}

export enum SupportedPlatform
{
    Unsupported    = 0,
    IE             = 1,
    GoogleChrome   = 2,
    Firefox_Applet = 3,
    Firefox_WebExt = 4
}

/** Script control para utilização da assinatura digital
 */
export class SignerCore
{
    private static instance: SignerCore;

    private useWebExt: boolean = false;
    
    private webExtReady: boolean = false;

    private webExtPingInterval: number;

    private idGenerator: RequestIdGenerator;

    private javaSessionSequence: number = 0;

    private javaSessionsCount: number = 0;

    private javaSessions: { [id: string] : AbstractJavaSession } = {};

    private webExtMessageBroker: WebExtMessageBroker;

    private handleWindowMessageHandler: (MessageEvent) => void;

    /** Construtor
     * @param coreHtmlElementId             Id do elemento html
     * @param requestSourceId               Id da origem da requisição
     * @param jnlpPath                      Caminho do applet
     * @param signatureResponseMaxInterval  Tempo máximo, em milisegundos, para esperar resposta da requisição de assinatura
     * @param statisticsEnabled             Define se coleta de estatísticas será habilitada
     * @param sessionIsolationEnabled       Define se sessão isolada será habilitadas
     * @param logEnabled                    Define se o log de assinatura será habilitado
     * @param forceAppletUsage              Define se deve utilizar o applet obrigatóriamente
     * @param framed                        Define se este componente está sendo utilizando dentro de um iframe
     * @param appletSupportChecker          Função para verificar se o browser suporta utilização de applet
     */
    constructor(
        private coreHtmlElementId: string,
        private requestSourceId: string,
        private jnlpPath: string,
        private signatureResponseMaxInterval: number = 1000,
        private statisticsEnabled: boolean = false,
        private sessionIsolationEnabled: boolean = true,
        private logEnabled: boolean = false,
        private forceAppletUsage: boolean = false,
        private framed: boolean = false,
        private appletSupportChecker: ()=>boolean = null
    ) {
        // Se não informado considera que sempre suporta
        if (!this.appletSupportChecker) this.appletSupportChecker = function() { return true; };
    }

    /**
     * Retorna se este componente está configurado para rodar dentro de um iframe
     */
    get_framed(): boolean { return this.framed; }
    /**
     * Seta configuração que habilita este componente rodar dentro de um iframe
     * @param framed Indicador se está dentro de iframe
     */
    set_framed(framed: boolean): void { this.framed = framed; }

    /** Busca id da origem das requisições
     * @returns Id da origem das requisições
     */
    get_requestSourceId() : string {
        return this.requestSourceId;
    }

    /** Define id da origem das requisições
     * @param requestSourceId  Novo id da origem das requisições
     */
    set_requestSourceId(requestSourceId: string) : void {
        this.requestSourceId = requestSourceId;
    }

    /** Busca caminho do applet
     * @returns caminho do applet
     */
    get_jnlpPath() : string {
        return this.jnlpPath;
    }

    /** Define caminho do applet
     * @param Novo caminho do applet
     */
    set_jnlpPath(jnlpPath: string) : void {
        this.jnlpPath = jnlpPath;
    }

    /** Verifica se o log de assinatura está habilitado
     * @returns Verdadeiro se o log de assinatura está habilitado, senão falso
     */
    get_logEnabled() : boolean {
        return this.logEnabled;
    }

    /** Define se log de assinatura deve ser habilitado
     * @param enabled  Verdadeiro para habilitar log de assinatura, senão falso
     */
    set_logEnabled(enabled: boolean) : void {
        this.logEnabled = enabled;
    }

    /** Verifica se a coleta de estatísticas está habilitada
     * @returns Verdadeiro se a coleta de estatísticas está habilitada, senão falso
     */
    get_statisticsEnabled() : boolean {
        return this.statisticsEnabled;
    }

    /** Define se deve habilitar a coleta de estatísticas
     * @param enabled  Verdadeiro para habilitar a coleta de estatísticas, senão falso
     */
    set_statisticsEnabled(enabled: boolean) : void {
        this.statisticsEnabled = enabled;
    }

    /** Verifica se deve utilizar sessão isolada
     * @returns Verdadeiro se deve utilizar sessão isolada, senão falso
     */
    get_sessionIsolationEnabled() : boolean {
        return this.sessionIsolationEnabled;
    }
    
    /** Define se deve utilizar sessão isolada
     * @param enabled  Verdadeiro para forçar utilização de sessão isolada, senão falso
     */
    set_sessionIsolationEnabled(enabled: boolean) : void {
        this.sessionIsolationEnabled = enabled;
    }

    /** Verifica se obriga utilização do applet
     * @return Verdadeiro se a utilização do applet é obrigatória, senão falso
     */
    get_forceAppletUsage() : boolean {
        return this.forceAppletUsage;
    }
    
    /** Define se obriga utilização do applet
     * @param forceAppletUsage  Verdadeiro para forçar utilização do applet, senão falso
     */
    set_forceAppletUsage(forceAppletUsage: boolean) : void {
        this.forceAppletUsage = forceAppletUsage;
    }

    /** Busca tempo máximo, em milisegundos, para esperar resposta de requisição de assinatura
     * @returns Tempo em milisegundos
     */
    get_signatureResponseMaxInterval() : number {
        return this.signatureResponseMaxInterval;
    }
    
    /** Define tempo máximo, em milisegundos, para esperar resposta de requisição de assinatura
     * @param signatureResponseMaxInterval  Tempo em milisegundos
     */
    set_signatureResponseMaxInterval(signatureResponseMaxInterval: number) : void {
        this.signatureResponseMaxInterval = signatureResponseMaxInterval;
    }

    /** Busca gerador de id de requisição
     * @returns Instância do objeto responsável pela geração dos ids de requisições
     */
    get_requestIdGenerator() : RequestIdGenerator {
        return this.idGenerator;
    }

    /** Verifica se utiliza extensão web
     * @returns Verdadeiro se utiliza extensão web, senão falso
     */
    get_useWebExtension() : boolean {
        return this.useWebExt;
    }
    
    /** Verifica se extensão web está pronta para utilização
     * @returns Verdadeiro se extensão está pronta para utilização, senão falso
     */
    get_webExtensionReady() : boolean {
        return this.webExtReady;
    }

    static getInstance() : SignerCore {
        return SignerCore.instance;
    }

    static detectPlatform() : SupportedPlatform
    {
        //browsername em lowercase
        const browserName: string = window.navigator.userAgent.toLowerCase();

        //edge ou opera (prefixos específicos)
        let m:RegExpExecArray = (/\s(edge|opr)\/[\d\.]+/).exec(browserName);
        if (m) return SupportedPlatform.Unsupported;

        //se for o chrome vai ter tal fragmento na versão
        if ((/\schrome\/[\d\.]+/).test(browserName)) return SupportedPlatform.GoogleChrome;

        //se for safari vai ter isso escrito
        if ((/\ssafari\/[\d\.]+/).test(browserName)) return SupportedPlatform.Unsupported;

        //se é firefox vai ter "gecko" com versão
        if ((/\sgecko\/[\d]+/).test(browserName)) {
            //pega versão do firefox
            m = (/rv:(\d+)\.\d/).exec(browserName);
            if (m) {
                if (Number(m[1]) >= 52) return SupportedPlatform.Firefox_WebExt;
            }
            //firefox com applet
            return SupportedPlatform.Firefox_Applet;
        }

        //se tiver propriedade "documentMode", vamos acreditar que é Internet Explorer
        if ("documentMode" in window.document) return SupportedPlatform.IE;

        //não conhecemos o navegador
        return SupportedPlatform.Unsupported;
    }

    //sub:_throwCustomError
    private throwCustomError(message: string, name: string) {
        const e = new Error(message);
        e.name = name;
        throw e;
    }

    //sub:_queryExtensionVersionInternal
    private queryExtensionVersionInternal(callback: QueryExtensionVersionCallback, bypassCache: boolean) : void {
        const coreRef: SignerCore = this;
        this.newJavaSession((session: WebExtSession) => {
            //se for uma sessão única que criamos para esta operação, flagga que pode fazer skip do comando de release de
            //sessão, uma vez que este tipo de request não gera necessariamente uma sessão na app nativa e vamos usar a sessão só para isso
            if (!session.is_shared()) {
                session.set_skipSessionRelease(true);
            }
            //envia comando de requisição de versão
            session.getExtensionVersion(function() {
                //destrói sessão que usamos para isso
                coreRef.destroySession(session);
                //invoca callback com os argumentos recebidos na função
                callback.apply(window, arguments);
            }, bypassCache);
        });
    }

    /** Busca versão da extensão sendo utilizada
     * @param callback          Callback para processamento da resposta
     * @param bypassCache       Define se deve ser limapado o cache antes da consulta da versão
     * @param readinessTimeout  Tempo, em milisegundos, para esperar criação da sessão
     */
    queryExtensionVersion(callback : QueryExtensionVersionCallback, bypassCache: boolean = false, readinessTimeout: number = 3000) : void {
        //verifica se estamos usando a extensão web
        if (!this.get_useWebExtension()) {
            throw new Error("Não é possível pedir versão da instalação da extensão web quando este objeto não estiver dependendo de tal");
        }

        //se extensão já estiver ready, invoca o método de pegar versão diretamente
        if (this.get_webExtensionReady()) {
            return this.queryExtensionVersionInternal(callback, bypassCache);
        }
        
        //vamos esperar até a extensão ficar pronta (3 segundos no máximo, por default). Vamos fazer uma request por décimo de segundo.
        readinessTimeout = readinessTimeout || 3000;
        const interval: number = 100;

        //contexto da espera
        const ctx = {
            intervalRef: null,
            thisRef: this,
            attempts: 0,
            attempts_limit: Math.ceil(readinessTimeout / interval)
        };

        //função de espera até extensão estiver "ready"
        const checkMethod = () => {
            //verifica se está pronta
            const isReady: boolean = ctx.thisRef.get_webExtensionReady();

            //se ainda não chegamos em um estado terminal de nossa espera, vamos deixar para a próxima
            const keepWaiting: boolean = !isReady && (++ctx.attempts < ctx.attempts_limit);
            if (keepWaiting) return;

            //limpa nosso interval
            clearInterval(ctx.intervalRef);
            ctx.intervalRef = null;

            //se extensão está pronta, vamos invocar o método de pedir versão da extensão
            if (isReady) return ctx.thisRef.queryExtensionVersionInternal(callback, bypassCache);

            //se extensão não respondeu em tempo hábil, ela não está instalada (vamos invocar o callback descrevendo este erro)
            callback({
                error: {
                    type: "web_error.appNotInstalled",
                    msg: "Extensão web não instalada"
                }
            });
        };

        //inicia intervalo de probe se está pronta a extensão
        ctx.intervalRef = setInterval(checkMethod, interval);
    }

    //sub: _detectExtension
    private detectExtension(platform:SupportedPlatform) : void
    {
        //se for para usar applet mesmo (forma forçada)
        if (this.get_forceAppletUsage()) {
            this.useWebExt = false;
            return;
        }

        //caso for navegador legacy, que não suporta extensões deve ser usado applet
        switch (platform)
        {
            case SupportedPlatform.Firefox_Applet:
            case SupportedPlatform.IE:
                this.useWebExt = false;
                return;
        }

        //vamos tentar com extensão mesmo
        this.useWebExt = true;
    }

    //sub: _disposeWebExtensionPing
    private disposeWebExtensionPing() : void {
        clearInterval(this.webExtPingInterval);
        this.webExtPingInterval = null;
    }

    //sub: _initializeWebExtensionPing
    /**
     * Inicializa mecanismo de ping para extensão
     * @param messageBroker Message broker para eventos de extensão
     */
    private initializeWebExtensionPing(messageBroker: EventEmitter) : void {
        //envia primeiro ping
        WebExtSession.sendPingCommand(messageBroker);

        //configuração do contexto de ping
        const ctx = {
            interval: 100,
            thisRef: this,
            attempts: 0,
            //tenta por 1 minuto (10x por segundo x 60 segundos
            attempts_limit: 10 * 60
        };

        //de tempos em tempos, envia um ping para a extensão web
        const pingMethod = () => {
            //variável que indica se temos que parar o processo de ping (primeiro critério é já termos uma resposta positiva do ping)
            let stopPing: boolean = ctx.thisRef.webExtReady;

            //executa ping, se necessário
            if (!stopPing) {
                WebExtSession.sendPingCommand(messageBroker);
                //contabiliza execução e avalia se não estourou o limite de execuções
                stopPing = (++ctx.attempts >= ctx.attempts_limit);
            }

            //verifica se deve parar o processo de ping
            if (stopPing) ctx.thisRef.disposeWebExtensionPing();
        };

        //registra chamadas em intervalo
        this.webExtPingInterval = window.setInterval(pingMethod, ctx.interval);
    }

    //sub: _handleExtensionPingResponse
    private handleExtensionPingResponse() : void {
        //verifica se já não foi feito o tratamento de extensão web pronta para uso
        if (this.webExtReady) return;

        //marca como pronta para uso a extensão web e faz dispose do processo de ping na extensão
        this.webExtReady = true;
        this.disposeWebExtensionPing();

        //percorre sessões sendo tratadas e notifica que elas estão ready
        for (let key in this.javaSessions)  {
            const sess: AbstractJavaSession = this.javaSessions[key];
            sess.internalNotifyReadiness();
        }
    }

    /** Handler para mensagem de applet pronto para utilização
     * @param javaSessionId  Id da sessão 
     */
    internalHandleAppletReadyResponse(javaSessionId: string) : void {
        //pega sessão java com o ID passado
        const sess = <AppletSession>this.javaSessions[javaSessionId];
        if (!sess) return console.error("[SignerCore] Recebido 'ready' de um applet de sessão desconhecida: " + javaSessionId);

        //invoca método notificando que tal sessão está "ready"
        sess.internalNotifyReadiness();
    }

    /** Handler para resposta de requisições. Verifica se resposta recebida é de uma requisição conhecida
     * @param respObj  Objeto de resposta da requisição
     */
    internalHandleResponse(respObj: any) : void {
        //TODO: tipar 'respObj' quando souber certinho o que é

        //extrai ID da sessão à partir do ID da response
        const javaSessionId: string = this.get_requestIdGenerator().extractJavaSessionId(respObj.id);

        //pega sessão sendo gerenciada por este controle
        const javaSession: AbstractJavaSession = this.javaSessions[javaSessionId];
        if (!javaSession) {
            //se estivermos usando extensão do chrome e a mensagem for de release session, é normal não termos mais sessão java registrada aqui
            if (respObj.operation === CoreConstants.OP_EXT_RELEASE_SESSION) return;
            //loga erro que não encontrou sessão para a mensagem
            console.error("[SignerCore] Recebida resposta para sessão inexistente");
            return console.dir(respObj);
        }

        //repassa mensagem para a sessão
        javaSession.internalHandleResponse(respObj);
    }

    //sub: _handleWindowMessage
    private handleWindowMessage(responseMessage: CoreResponseModel) : void
    {
        //se for uma resposta para comando de "ping", chama tratamento adequado, caso contrário chama tratamento de respostas de assinatura
        if (responseMessage.operation === CoreConstants.OP_EXT_PING) {
            this.handleExtensionPingResponse();
        }
        else { this.internalHandleResponse(responseMessage); }
    }

    //sub: _initializeExtensionMessageListening
    /**
     * Inicializa escuta de eventos vindos da extensão do navegador
     * @param messageBroker Message broker para comunicação com extensão do navegador
     */
    private initializeExtensionMessageListening(messageBroker: EventEmitter) : void {
        //inicializa handler de eventos para mensagens na janela
        if (!this.handleWindowMessageHandler) {
            this.handleWindowMessageHandler = this.handleWindowMessage.bind(this);
            messageBroker.on(CoreConstants.RESPONSE_MSG_TYPE, this.handleWindowMessageHandler);
        }
    }

    //sub: _disposeExtensionMessageListening
    /**
     * Faz dispose na escuta de eventos vindos da extensão do navegador
     * @param messageBroker Message broker para comunicação com extensão do navegador
     */
    private disposeExtensionMessageListening(messageBroker: EventEmitter) : void {
        //remove listener de eventos de mensagens de janela
        if (this.handleWindowMessageHandler) {
            messageBroker.off(CoreConstants.RESPONSE_MSG_TYPE, this.handleWindowMessageHandler);
            this.handleWindowMessageHandler = null;
        }
    }

    //sub: _makeNewJavaSessionId
    private makeNewJavaSessionId(shared: boolean) : string {
        //se ID para uma sessão compartilhada, temos isso em uma constante
        if (shared) return CoreConstants.SHARED_JAVA_SESSION_ID;

        //pega um ID de sessão sequencial e retorna ele
        const seq: number = ++this.javaSessionSequence;
        return seq.toString();
    }

    //sub: _destroyAllSessions
    private destroyAllSessions() : void {
        //zera número de sessões
        this.javaSessionsCount = 0;

        //percorre todas as sessões fazendo dispose nelas
        for (let key in this.javaSessions) {
            //pega session e faz dispose nela, depois nula referência
            this.javaSessions[key].internalDispose();
            this.javaSessions[key] = null;
        }
    }

    //sub: _createNewJavaSession
    /**
     * @throws {Error} lança erro com name "CoreConstants.ERR_JAVA_APPLET_NOT_SUPPORTED" quando browser não consegue acessar o java applet
     */
    private createNewJavaSession(javaSessionId: string) : AbstractJavaSession {
        //verifica que tipo de sessão devemos criar (classe) e instancia objeto
        let sessionObj: AbstractJavaSession;
        if (this.useWebExt) {
            sessionObj = new WebExtSession(javaSessionId, this.statisticsEnabled, this.signatureResponseMaxInterval, this.idGenerator, this.webExtMessageBroker.get_eventEmitter());
        }
        else {
            // Verifica se browser tem suporte para utilizar java applet (java instalado e browser ainda suporta)
            if (!this.appletSupportChecker()) {
                this.throwCustomError("Navegador sem suporte à java applets", CoreConstants.ERR_JAVA_APPLET_NOT_SUPPORTED);
            }

            //id para o applet e elemento DOM simbolizando o core
            const appletId = `${this.coreHtmlElementId}_${javaSessionId}_applet`;
            const coreDomElement = document.getElementById(this.coreHtmlElementId);

            sessionObj = new AppletSession(appletId, this.jnlpPath, this.logEnabled, coreDomElement, javaSessionId, this.statisticsEnabled, this.signatureResponseMaxInterval, this.idGenerator);
        }
        
        //adiciona na lista de sessões
        this.javaSessions[javaSessionId] = sessionObj;
        ++this.javaSessionsCount;
        //retorna objeto criado
        return sessionObj;
    }

    /** Cria nova sessão java
     * @param callbackMethod  Callback para processamento do retorno
     */
    newJavaSession(callbackMethod: (session: AbstractJavaSession) => void) : number {
        //verifica se pode usar uma sessão compartilhada, e determina ID para a nova sessão
        const useShared: boolean = !this.get_sessionIsolationEnabled();
        const sessionId: string = this.makeNewJavaSessionId(useShared);
        
        //se pode usar compartilhada, verifica se já não temos criada tal sessão
        let session: AbstractJavaSession = useShared ? this.javaSessions[sessionId] : null;

        //se não podemos reutilizar uma sessão, vamos criar uma nova
        if (!session) session = this.createNewJavaSession(sessionId);

        //adiciona método de callback na lista de eventos de 'ready' da sessão
        session.onReady(callbackMethod);

        //situações onde pedimos para o evento de ready ser disparado imediatamente:
        //1) a sessão já é Ready (sessão compartilhada)
        //2) estivermos utilizando extensão web, e a extensão já respondeu ao ping
        if (session.is_ready() || this.webExtReady) {
            //bota dentro de um setTimeout() só para ficar agendado para próximo poll da EventQueue do javascript
            return window.setTimeout(function () {
                session.internalNotifyReadiness();
            }, 0);
        }
    }

    /** Finaliza uma sessão java
     * @param session  Sessão a ser finalizada
     */
    destroySession(session: AbstractJavaSession) : void {
        //se a sessão for shared, não destrói ela
        if (session.is_shared()) return;

        //pega sessão pelo ID (se não encontrar, dispara erro)
        const sessionId: string = session.get_javaSessionId();
        if (!this.javaSessions[sessionId]) throw new Error("Sessão passada não gerenciada por este objeto");

        //remove da lista de sessões
        delete this.javaSessions[sessionId];
        --this.javaSessionsCount;

        //faz dispose do objeto
        session.internalDispose();
    }

    /** Realiza configurações iniciais do controle de assinatura
     */
    initialize() : void {
        if (SignerCore.instance) {
            throw new Error("Não foi possível registrar " + this.coreHtmlElementId + " como singleton da classe SignerCore: outra instância já estava ocupando tal posição");
        }
        SignerCore.instance = this;

        //detecta plataforma em que estamos rodando
        const platform:SupportedPlatform = SignerCore.detectPlatform();
        if (platform === SupportedPlatform.Unsupported)
            this.throwCustomError("Plataforma não suportada pelo assinador", CoreConstants.ERR_PLATFORM_NOT_SUPPORTED);

        //cria gerador de ID de request
        this.idGenerator = new RequestIdGenerator(this.get_requestSourceId());

        //detecta se temos que usar extensão web ou applet java
        this.detectExtension(platform);
        
        //inicializações para quando usar extensão no navegador
        if (this.useWebExt) {
            //cria message broker para eventos
            this.webExtMessageBroker = this.framed ? new IFrameWebExtMessageBroker(window) : new DefaultWebExtMessageBroker(window);
            this.webExtMessageBroker.init();
            //inicializa listening de mensagens da extensão
            this.initializeExtensionMessageListening(this.webExtMessageBroker.get_eventEmitter());
            //inicializa envio de "ping" para extensão web
            this.initializeWebExtensionPing(this.webExtMessageBroker.get_eventEmitter());
        }
    }

    /** Limpa referências do objeto
     */
    dispose(): void {
        //remove de singleton
        if (SignerCore.instance === this) {
            SignerCore.instance = null;
        }

        //faz dispose de pingamento da extensão (se ela tiver sido iniciada) e do tratamento de mensagens de janela (quando usa extensão do chrome)
        if (this.useWebExt) {
            this.disposeExtensionMessageListening(this.webExtMessageBroker.get_eventEmitter());
            this.disposeWebExtensionPing();

            this.webExtMessageBroker.dispose();
            this.webExtMessageBroker = null;
        }
        
        //destrói todas as sessões
        this.destroyAllSessions();
    }
}



// WEBPACK FOOTER //
// ./lib/core.ts